import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  ErrorCircleRegular,
  InfoRegular,
  ShieldLockRegular,
} from '@fluentui/react-icons';
import type { ReactNode } from 'react';
import { EmptyState, LoadingState } from '#src/shared/ui';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '100%',
    boxSizing: 'border-box',
  },
  // Fora do shell (login, erro de sessão), com o mesmo fundo da aplicação.
  fullPage: {
    backgroundImage: `linear-gradient(180deg, ${tokens.colorBrandBackground2} -60%, ${tokens.colorNeutralBackground2} 45%)`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

interface StatusScreenProps {
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
  readonly loading?: boolean;
  readonly tone?: 'info' | 'error' | 'denied';
  /** Ocupa a janela inteira, fora do shell. */
  readonly fullPage?: boolean;
}

const ICONS = {
  info: <InfoRegular />,
  error: <ErrorCircleRegular />,
  denied: <ShieldLockRegular />,
};

/** Tela de estado: carregando, erro, sem acesso, página inexistente. */
export function StatusScreen({
  title,
  description,
  action,
  loading = false,
  tone = 'info',
  fullPage = false,
}: StatusScreenProps) {
  const styles = useStyles();
  const className = mergeClasses(styles.root, fullPage && styles.fullPage);

  return (
    <div className={className} role={tone === 'error' ? 'alert' : 'status'}>
      {loading ? (
        <LoadingState label={title} />
      ) : (
        <EmptyState
          icon={ICONS[tone]}
          tone={tone === 'info' ? 'neutral' : 'danger'}
          title={title}
          {...(description === undefined ? {} : { description })}
          {...(action === undefined ? {} : { action })}
        />
      )}
    </div>
  );
}
